import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, BookOpen, Download, Printer } from "lucide-react";
import { PosterScanLine, PosterNetworkInfo, PosterDidYouKnow, PosterHeadline } from "../components/extract/PosterInfo";
import { fetchPartner, partnerPublicUrl, extractGenreLabel } from "../lib/api";

const Rule = () => <div className="h-px bg-[#D1CFC7] my-2" />;

export default function PartnerPoster() {
  const { code } = useParams();
  const [partner, setPartner] = useState(null);
  const [error, setError] = useState(false);
  const url = partnerPublicUrl(code);
  useEffect(() => { fetchPartner(code).then(setPartner).catch(() => setError(true)); }, [code]);

  const downloadQR = () => {
    const a = document.createElement("a");
    a.href = document.getElementById("partner-qr-canvas").toDataURL("image/png");
    a.download = `qr-${code}.png`;
    a.click();
  };

  if (error) return <p data-testid="partner-poster-error" className="p-12 font-body text-[#9E4735]">Partenaire introuvable.</p>;
  if (!partner) return <p className="p-12 font-body text-[#757575]">Chargement…</p>;

  return (
    <div data-testid="page-partner-poster" className="min-h-screen bg-[#E8E4D9] py-10 px-4 print:p-0 print:bg-white">
      <div className="no-print max-w-[210mm] mx-auto mb-6 flex flex-wrap items-center gap-3">
        <Link to="/admin" data-testid="partner-poster-back-link" className="flex items-center gap-2 text-sm font-body text-[#1A1A1A] mr-auto"><ArrowLeft size={14} /> Tableau de bord</Link>
        <button onClick={downloadQR} data-testid="partner-poster-download-qr" className="flex items-center gap-2 border border-[#1A1A1A] px-4 py-2 text-sm font-body hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors"><Download size={14} /> QR code (PNG)</button>
        <button onClick={() => window.print()} data-testid="partner-poster-print-button" className="flex items-center gap-2 bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body hover:bg-[#1F2D21] transition-colors"><Printer size={14} /> Imprimer l'affiche</button>
        <QRCodeCanvas id="partner-qr-canvas" value={url} size={1024} marginSize={4} className="hidden" />
      </div>
      <Sheet partner={partner} url={url} />
    </div>
  );
}

const PartnerLogo = ({ partner }) => {
  const [failed, setFailed] = useState(false);
  if (partner.logo_url && !failed)
    return <img src={partner.logo_url} alt={partner.name} onError={() => setFailed(true)} data-testid="partner-poster-logo" className="h-full w-auto max-w-[40%] object-contain" />;
  return <p data-testid="partner-poster-name" className="font-serif italic text-xl sm:text-3xl leading-tight text-[#2C402E] text-right max-w-[50%]">{partner.name}</p>;
};

const Sheet = ({ partner, url }) => {
  const genres = [...new Set(partner.extracts.map((e) => e.genre).filter(Boolean))].map(extractGenreLabel);
  return (
    <article data-testid="partner-poster-sheet" className="poster-sheet mx-auto bg-[#FAF9F6] w-full max-w-[210mm] sm:aspect-[210/297] shadow-xl print:shadow-none p-[5%] flex flex-col text-[#1A1A1A]">
      <div className="border border-[#2C402E]/40 flex-1 flex flex-col justify-evenly gap-4 sm:gap-0 px-[8%] py-[4%]">
        <div className="flex items-center justify-between gap-4 h-[64px] sm:h-[120px]">
          <img src="/cled-logo-cropped.png" alt="Challenges Littéraires Éditions" className="h-[70%] w-auto object-contain mix-blend-multiply" />
          <PartnerLogo partner={partner} />
        </div>
        <Rule />
        <div className="text-center">
          <PosterHeadline count={partner.extracts.length} />
          <PosterDidYouKnow />
        </div>
        <Rule />
        <div className="flex flex-col items-center text-center">
          <div className="p-4 sm:p-5 bg-white border border-[#D1CFC7]">
            <QRCodeSVG value={url} size={512} marginSize={0} data-testid="partner-poster-qr" className="w-[38vw] max-w-[240px] sm:w-[240px] h-auto print:w-[52mm]" />
          </div>
          <PosterScanLine />
          {genres.length > 0 && <p data-testid="partner-poster-genres" className="font-serif italic text-base sm:text-xl text-[#2C402E] mt-[3%]">{genres.join(" · ")}</p>}
        </div>
        <Rule />
        <div className="text-center font-body">
          <PosterNetworkInfo />
          <p className="text-[9px] sm:text-[10px] text-[#757575] mt-[2%] break-all" data-testid="partner-poster-url">{url.replace(/^https?:\/\//, "")}</p>
        </div>
      </div>
    </article>
  );
};
